import Cookies from 'js-cookie';

interface AuthUser {
  id: string;
  email: string;
  username: string;
}

interface LoginResponse {
  token: string;
  user: AuthUser;
}

const API_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'auth_token';

/**
 * Сохранить токен аутентификации в куки
 */
export const saveToken = (token: string): void => {
  Cookies.set(TOKEN_KEY, token, { expires: 1, sameSite: 'strict' });
};

/**
 * Получить токен аутентификации из куки
 */
export const getToken = (): string | undefined => {
  return Cookies.get(TOKEN_KEY);
};

/**
 * Удалить токен аутентификации из куки
 */
export const removeToken = (): void => {
  Cookies.remove(TOKEN_KEY);
};

/**
 * Получить заголовки авторизации для запросов к API
 */
export const getAuthHeaders = (): Record<string, string> | {} => {
  const token = getToken();
  
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  
  return {};
};

/**
 * Проверить, авторизован ли пользователь
 */
export const isAuthenticated = (): boolean => {
  return !!getToken();
};

/**
 * Регистрация нового пользователя
 */
export const register = async (
  email: string, 
  password: string, 
  username: string
): Promise<LoginResponse> => {
  const response = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, username }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Ошибка при регистрации');
  }

  const data = await response.json();
  saveToken(data.token);
  
  return data;
};

/**
 * Вход пользователя
 */
export const login = async (
  email: string, 
  password: string
): Promise<LoginResponse> => {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Ошибка при входе');
  }

  const data = await response.json();
  saveToken(data.token);
  
  return data;
};

/**
 * Выход пользователя
 */
export const logout = (): void => {
  removeToken();
};

/**
 * Получить данные текущего пользователя
 */
export const getCurrentUser = async (): Promise<AuthUser | null> => {
  if (!isAuthenticated()) {
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      removeToken();
      return null;
    }

    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error('Ошибка при получении данных пользователя:', error);
    removeToken();
    return null;
  }
};

/**
 * Обменять OAuth код на токен для Яндекса
 */
export const exchangeCodeForToken = async (
  provider: 'yandex', 
  code: string
): Promise<LoginResponse> => {
  const response = await fetch(`${API_URL}/auth/${provider}/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Ошибка при авторизации через ${provider}`);
  }

  const data = await response.json();
  saveToken(data.token);
  
  return data;
};

/**
 * Получить URL для авторизации через ВКонтакте
 */
export const getVkAuthUrl = (): string => {
  return `${API_URL}/auth/vk`;
};

/**
 * Получить URL для авторизации через Яндекс
 */
export const getYandexAuthUrl = (): string => {
  return `${API_URL}/auth/yandex`;
};

// Создаем объект сервиса
const authService = {
  saveToken,
  getToken,
  removeToken,
  getAuthHeaders,
  isAuthenticated,
  login,
  register,
  logout,
  getCurrentUser,
  exchangeCodeForToken,
  getVkAuthUrl,
  getYandexAuthUrl
};

// Экспортируем объект по умолчанию
export default authService; 